import React, { useState, useEffect } from 'react';
import './Pricing.css';
import { useRevit } from '../../contexts/RevitContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { SignOutButton, useUser, useAuth } from '@clerk/clerk-react';
import Icon from '../Icon';
import axios from 'axios';

const Pricing = () => {
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const { username, userEmail } = useRevit();
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useUser();
    const { getToken } = useAuth();
    const [billingCycle, setBillingCycle] = useState('annual');
    const [hasPastDueSubscription, setHasPastDueSubscription] = useState(false);
    const [isCheckingPastDue, setIsCheckingPastDue] = useState(true);

    useEffect(() => {
        const checkPastDueSubscription = async () => {
            if (!username) return;

            setIsCheckingPastDue(true);
            try {
                const token = await getToken();
                const response = await axios.get(
                    `https://${SERVER_URL}/api/check-past-due-subscription/${username}`,
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );
                setHasPastDueSubscription(response.data.hasPastDueSubscription);
            } catch (error) {
                console.error('Error checking past due subscription:', error);
            } finally {
                setIsCheckingPastDue(false);
            }
        };

        checkPastDueSubscription();
    }, [username, getToken, SERVER_URL]);

    const openBillingPortal = async () => {
        try {
            const response = await fetch(`https://${SERVER_URL}/api/create-billing-portal-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ userId: username }),
            });

            if (!response.ok) {
                throw new Error('Failed to create billing portal session');
            }

            const { url } = await response.json();
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error opening billing portal:', error);
        }
    };

    const handlePlanSelection = (planType) => {
        const checkoutPlanType = billingCycle === 'annual' ?
            `annual${planType.charAt(0).toUpperCase() + planType.slice(1)}` :
            `monthly${planType.charAt(0).toUpperCase() + planType.slice(1)}`;
        createCheckoutSession(checkoutPlanType);
    }

    const createCheckoutSession = async (planType) => {
        setIsLoading(true);
        try {
            const response = await fetch(`https://${SERVER_URL}/api/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    userId: username, 
                    planType: planType, 
                    email: userEmail 
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to create checkout session');
            }

            const { url } = await response.json();
            window.location.href = url;
        } catch (error) {
            console.error('Error creating checkout session:', error);
            setIsLoading(false);
        }
    };

    const toggleBillingCycle = () => {
        setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly');
    };

    const PricingFeature = ({ text }) => (
        <div className="pricing-feature">
            <FontAwesomeIcon icon={faCheck} className="feature-icon" />
            <span>{text}</span>
        </div>
    );

    return (
        <div className="pricing-container">
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-text">Loading checkout...</div>
                </div>
            )}
            {isCheckingPastDue && (
                <div className="loading-overlay">
                    <div className="loading-text">Checking subscription status...</div>
                </div>
            )}

            {!isCheckingPastDue && hasPastDueSubscription ? (
                <>
                    <h1>Payment Failed</h1>
                    <p className="pricing-text">
                        Your latest subscription payment failed. Please update your payment information to continue using RevTrack.
                    </p>
                    <button
                        className="continue-button"
                        onClick={openBillingPortal}
                        style={{ marginTop: '20px' }}
                    >
                        Update Payment Information
                    </button>

                    <div className="loading-user-section">
                        <div className="loading-user-info">
                            <span className="loading-user-email">{user?.primaryEmailAddress?.emailAddress}</span>
                            <SignOutButton>
                                <Icon name="logout" size={20} color="white" style={{ cursor: 'pointer' }} />
                            </SignOutButton>
                        </div>
                    </div>
                </>
            ) : !isCheckingPastDue && (
                <>
                    <h1>It seems your plan has expired.</h1>
            <p className="pricing-text">Think this is a mistake? You have <b>almost certainly logged in with the wrong email.</b> Please see the currently logged in email in the bottom left and double & quadruple check that it is the correct one.</p>

            <p className="pricing-text">By subscribing to RevTrack, you agree to our <a href="https://getrevtrack.com/terms-conditions/" target="_blank" rel="noreferrer" style={{ color: 'white', fontWeight: 'bold', textDecoration: 'underline' }}>Terms of Service</a>. Cancel anytime.</p>
            
            <div className="billing-toggle">
            <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
                <label className="switch">
                    <input 
                        type="checkbox" 
                        checked={billingCycle === 'annual'} 
                        onChange={toggleBillingCycle}
                    />
                    <span className="slider round"></span>
                </label>
            <span className={billingCycle === 'annual' ? 'active' : ''}>Annual</span>
            </div>

            <div className="pricing-options">
                <div className="pricing-card">
                    <h2>Essential</h2>
                    <div className="price">
                        <span className="currency">$</span>
                        <span className="amount">{billingCycle === 'annual' ? '792' : '99'}</span>
                        <span className="period">{billingCycle === 'annual' ? 'Yearly' : 'Monthly'}</span>
                    </div>
                    {billingCycle === 'annual' && (
                        <div className="save-badge">4 months free!</div>
                    )}
                    <div className="features-list">
                        <PricingFeature text="Track up to 5 offers/sign up pages" />
                        <PricingFeature text="One-Click Channel-Wide Link Updates" />
                        <PricingFeature text="Identify Viewer Conversion Paths" />
                        <PricingFeature text="Performance Dashboard" />
                    </div>
                    <button className="continue-button" onClick={() => handlePlanSelection('essential')}>Continue Tracking</button>
                </div>

                <div className="pricing-card highlight">
                    <h2>Pro</h2>
                    <div className="price">
                        <span className="currency">$</span>
                        <span className="amount">{billingCycle === 'annual' ? '1192' : '149'}</span>
                        <span className="period">{billingCycle === 'annual' ? 'Yearly' : 'Monthly'}</span>
                    </div>
                    {billingCycle === 'annual' && (
                        <div className="save-badge">4 months free!</div>
                    )}
                    <div className="features-list">
                        <PricingFeature text="Track unlimited offers/sign up pages" />
                        <PricingFeature text="One-Click Channel-Wide Link Updates" />
                        <PricingFeature text="Identify Viewer Conversion Paths" />
                        <PricingFeature text="Performance Dashboard" />
                        <PricingFeature text="Automated Viewer Comment Analysis" />
                        <PricingFeature text="AI Video Ideas Optimized for Conversions" />
                    </div>
                    <button className="continue-button" onClick={() => handlePlanSelection('pro')}>Continue Tracking</button>
                </div>
            </div>

            <div className="unsupported-notice">
                <p>
                    Note: we currently cannot support tracking for the following softwares. 
                    If you only use one of these, do not buy RevTrack as it won't work for you:
                </p>
                <div className="unsupported-list">
                    <div>
                        <FontAwesomeIcon icon={faTimes} className="unsupported-icon" />
                        Podia
                    </div>
                    <div>
                        <FontAwesomeIcon icon={faTimes} className="unsupported-icon" />
                        Skool
                    </div>
                    <div>
                        <FontAwesomeIcon icon={faTimes} className="unsupported-icon" />
                        Shopify
                    </div>
                </div>
            </div>
            
            <div className="loading-user-section">
                <div className="loading-user-info">
                    <span className="loading-user-email">{user?.primaryEmailAddress?.emailAddress}</span>
                    <SignOutButton>
                        <Icon name="logout" size={20} color="white" style={{ cursor: 'pointer' }} />
                    </SignOutButton>
                </div>
            </div>
                </>
            )}
        </div>
    );
};

export default Pricing;
